import React, {
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
  useCallback,
  ChangeEvent,
} from 'react';
import { IconBaseProps } from 'react-icons';
import { FiAlertCircle, FiEye, FiEyeOff } from 'react-icons/fi';
import { useField } from '@unform/core';
import VMasker from 'vanilla-masker';

import { Container, Error, EyePassword, VisuallyHiddenLabel } from './styles';
import { tokens } from '../../../design/tokens';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  icon?: React.ComponentType<IconBaseProps>;
  containerStyle?: Record<any, any>;
  enabled?: boolean;
  customBgColor?: string;
  customRadius?: string;
  customWidth?: string;
  mask?: {
    values: string | string[];
    maxLenght: number;
  };
  callback?(inputRef: HTMLInputElement): void;
}

const Input: React.FC<InputProps> = ({
  name,
  containerStyle = {},
  icon: Icon,
  enabled = true,
  customBgColor,
  customRadius,
  customWidth,
  callback,
  mask,
  ...rest
}) => {
  const [focus, setFocus] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const [showPassword, setShowPassword] = useState(true);

  const inputReference = useRef<HTMLInputElement>(null);

  const { fieldName, defaultValue, error, registerField } = useField(name);

  const errorId = `${name}-error`;

  const handleInputBlur = useCallback(() => {
    setFocus(false);
    // o operador !! é pra ver se existe algum valor na variável
    setHasValue(!!inputReference.current?.value);
  }, []);

  const handleInputFocus = useCallback(() => {
    setFocus(true);
  }, []);

  const handleInputEnter = useCallback(
    (event: any) => {
      if (event.key === 'Enter') {
        if (inputReference.current !== undefined) {
          callback && inputReference.current && callback(inputReference.current);
        }
      }
    },
    [callback],
  );

  const handleShowPassword = useCallback(() => {
    setShowPassword(!showPassword);

    if (inputReference.current) {
      showPassword
        ? (inputReference.current.type = 'text')
        : (inputReference.current.type = 'password');
    }
  }, [showPassword]);

  const inputHandler = (
    masks: string | string[],
    max: number,
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    const c = event.target;
    const v = c.value.replace(/\D/g, '');
    const m = c.value.length > max ? 1 : 0;
    VMasker(c).unMask();
    VMasker(c).maskPattern(masks[m]);
    c.value = VMasker.toPattern(v, masks[m]);
  };

  /**
   *  O defaultValue é uma prop passada la no Form chamada initialValue
   * pode ser passada assim:
   * initialData={{ name: 'Thiago' }}
   */

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputReference.current,
      path: 'value',
    });

    inputReference.current?.addEventListener('keypress', handleInputEnter);
  }, [fieldName, handleInputEnter, registerField]);

  useEffect(() => {
    if (mask) {
      inputReference.current?.addEventListener<any>(
        'input',
        e => inputHandler(mask.values, mask.maxLenght, e),
        false,
      );
    }
  }, [mask]);

  return (
    <Container
      style={containerStyle}
      hasError={!!error}
      isFocused={focus}
      hasValue={hasValue}
      data-testid="input-container"
      enabled={enabled}
      customBgColor={customBgColor}
      customRadius={customRadius}
      customWidth={customWidth}
    >
      {Icon && <Icon size={20} aria-hidden="true" />}
      <VisuallyHiddenLabel htmlFor={name}>
        {rest.placeholder ?? name}
      </VisuallyHiddenLabel>
      <input
        id={name}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        defaultValue={defaultValue}
        ref={inputReference}
        name={name}
        /* 'enabled={false}' pintava o campo de apagado mas o deixava
           editavel por teclado — 'pointer-events: none' so bloqueia o
           mouse. O atributo nativo e o que realmente desabilita. */
        disabled={!enabled}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        {...rest}
      />
      {error && (
        <Error id={errorId} role="alert" title={error}>
          <FiAlertCircle
            color={tokens.color.errorRed}
            size={20}
            aria-hidden="true"
          />
        </Error>
      )}
      {(name === 'password' || name === 'confirmPassword') && (
        <EyePassword
          type="button"
          onClick={handleShowPassword}
          aria-label={showPassword ? 'Mostrar senha' : 'Ocultar senha'}
        >
          {showPassword && <FiEye size={20} aria-hidden="true" />}
          {!showPassword && <FiEyeOff size={20} aria-hidden="true" />}
        </EyePassword>
      )}
    </Container>
  );
};

export default Input;
